import * as React from 'react'

import { clsx } from 'clsx'

import type { IconColor, IconSize } from '@buffer-mono/popcorn'
import {
  computeSize,
  computeStrokeWidth,
} from '@buffer-mono/popcorn/src/components/icons/helpers'
import type { IconComponentRef } from '@buffer-mono/popcorn/src/components/icons/helpers'
import styles from '@buffer-mono/popcorn/src/components/icons/Icon.module.css'
import textColorStyles from '@buffer-mono/popcorn/src/styles/atoms/text-color.module.css'

type IconProps = Omit<
  React.SVGAttributes<SVGElement>,
  'children' | 'stroke'
> & {
  /**
   * Size of the icon
   */
  size?: IconSize
  /**
   * Stroke width of the icon
   */
  stroke?: number
  /**
   * Color of the icon
   */
  color?: 'currentColor' | IconColor
  /**
   * Whether to use a gradient effect on the stroke
   * @default true
   */
  useGradient?: boolean
}

const CommentIcon = React.forwardRef(
  (
    { size, stroke, className, color, useGradient = true, ...props }: IconProps,
    ref: IconComponentRef,
  ) => {
    const iconName = 'comment'
    const computedSize = computeSize(size)
    const computedStrokeWidth = computeStrokeWidth(computedSize, stroke)
    const gradientId = React.useId()

    return (
      <svg
        viewBox="0 0 8.2 8.2"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={clsx(
          className,
          styles.base,
          color && textColorStyles[color],
        )}
        data-icon={iconName}
        aria-hidden="true"
        strokeWidth={computedStrokeWidth}
        width={computedSize}
        height={computedSize}
        ref={ref}
        {...props}
      >
        <path
          d="M7.75 5.41667C7.75 5.62295 7.66806 5.82078 7.52219 5.96664C7.37633 6.1125 7.1785 6.19444 6.97222 6.19444H2.30556L0.75 7.75V1.52778C0.75 1.3215 0.831944 1.12367 0.977806 0.977806C1.12367 0.831944 1.3215 0.75 1.52778 0.75H6.97222C7.1785 0.75 7.37633 0.831944 7.52219 0.977806C7.66806 1.12367 7.75 1.3215 7.75 1.52778V5.41667Z"
          stroke={useGradient ? `url(#${gradientId})` : 'currentColor'}
          strokeWidth={computedStrokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {useGradient && (
          <defs>
            <linearGradient
              id={gradientId}
              x1="4.25"
              y1="0.75"
              x2="4.25"
              y2="7.75"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="currentColor" stopOpacity="1" />
              <stop offset="1" stopColor="currentColor" stopOpacity="0.2" />
            </linearGradient>
          </defs>
        )}
      </svg>
    )
  },
)

CommentIcon.displayName = 'CommentIcon'

export { CommentIcon }
